import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const USERS_IN_GROUP_QUERY_KEY = "usersInGroup";

interface UseUsersInGroupParams {
  groupId: string | undefined;
  pageNumber?: number;
  pageSize?: number;
  search?: string;
}

interface UseUsersInGroupReturn {
  users: UserManagement_V1.UserWithCompanyName[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useUsersInGroup = ({
  groupId,
  pageNumber = 0,
  pageSize = 1000,
  search,
}: UseUsersInGroupParams): UseUsersInGroupReturn => {
  const userManagementClient = useUserManagementServiceV1();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [USERS_IN_GROUP_QUERY_KEY, groupId, pageNumber, pageSize, search],
    queryFn: () => {
      if (!groupId) throw new Error("Group ID is required");

      return userManagementClient.getUsersForUserGroup(
        groupId,
        pageNumber,
        pageSize,
        search,
      );
    },
    enabled: !!groupId,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(
    () => ({
      users: data ?? [],
      isLoading,
      error,
      refetch,
    }),
    [data, isLoading, error, refetch],
  );
};
